import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { getExceptionReport } from '../../../services/timeTrackingService';
import { convertFromUTC } from '../../../util/convert_timezone';

// Status filter options
const STATUS_OPTIONS = [
  { value: '', label: 'All Exceptions' },
  { value: 'flagged', label: 'Flagged Only' },
  { value: 'auto_closed', label: 'Auto-Closed Only' }
];

/**
 * ExceptionReport - Display flagged and auto-closed sessions
 *
 * Props:
 * - locationId: number - installer location ID
 * - dateRange: { startDate, endDate }
 */
class ExceptionReport extends Component {
  state = {
    summary: {
      total_exceptions: 0,
      flagged_count: 0,
      auto_closed_count: 0,
      total_exception_hours: 0
    },
    data: [],
    statusFilter: '',
    loading: true,
    error: null
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.dateRange.startDate !== this.props.dateRange.startDate ||
      prevProps.dateRange.endDate !== this.props.dateRange.endDate
    ) {
      this.loadData();
    }
  }

  loadData = async () => {
    const { locationId, dateRange } = this.props;
    const { statusFilter } = this.state;

    if (!locationId) return;

    try {
      this.setState({ loading: true, error: null });

      const result = await getExceptionReport(locationId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        status: statusFilter || undefined
      });

      this.setState({
        summary: result.summary || {
          total_exceptions: 0,
          flagged_count: 0,
          auto_closed_count: 0,
          total_exception_hours: 0
        },
        data: result.data || [],
        loading: false
      });
    } catch (error) {
      console.error('Error loading exception report:', error);
      this.setState({
        error: 'Failed to load exception data',
        loading: false
      });
    }
  };

  // Handle status filter change
  handleStatusFilterChange = (value) => {
    this.setState({ statusFilter: value }, () => this.loadData());
  };

  // Format hours with 2 decimals
  formatHours = (hours) => {
    if (hours === null || hours === undefined) return '0.00';
    const num = typeof hours === 'string' ? parseFloat(hours) : hours;
    return num.toFixed(2);
  };

  // Get status badge
  getStatusBadge = (status) => {
    switch (status) {
      case 'flagged':
        return <span className="badge badge-warning">Flagged</span>;
      case 'auto_closed':
        return <span className="badge badge-danger">Auto-Closed</span>;
      default:
        return <span className="badge badge-secondary">{status}</span>;
    }
  };

  // Format reason codes for display
  formatReasons = (reasons) => {
    if (!reasons) return '—';
    return reasons
      .split(',')
      .map(r => r.replace(/_/g, ' ').replace(/:/g, ': '))
      .join(', ');
  };

  // Render summary cards
  renderSummaryCards = () => {
    const { summary } = this.state;

    return (
      <div className="overview-cards">
        <div className="overview-card warning">
          <div className="number">{summary.total_exceptions || 0}</div>
          <div className="label">Total Exceptions</div>
        </div>
        <div className="overview-card warning">
          <div className="number">{summary.flagged_count || 0}</div>
          <div className="label">Flagged Sessions</div>
        </div>
        <div className="overview-card danger">
          <div className="number">{summary.auto_closed_count || 0}</div>
          <div className="label">Auto-Closed</div>
        </div>
        <div className="overview-card">
          <div className="number">{this.formatHours(summary.total_exception_hours)}</div>
          <div className="label">Exception Hours</div>
        </div>
      </div>
    );
  };

  render() {
    const { data, statusFilter, loading, error } = this.state;

    // Loading state
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-secondary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="text-muted mt-2 mb-0">Loading exception data...</p>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="text-danger p-3">
          <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      );
    }

    return (
      <div>
        {/* Summary Cards */}
        {this.renderSummaryCards()}

        {/* Filter */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span className="text-muted small">
              {data.length} exception{data.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div>
            <select
              className="form-control form-control-sm"
              value={statusFilter}
              onChange={(e) => this.handleStatusFilterChange(e.target.value)}
              style={{ width: '180px' }}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Empty state */}
        {data.length === 0 ? (
          <div className="text-center py-4" style={{ background: 'transparent' }}>
            <p className="text-muted mb-0">No exceptions for the selected period.</p>
          </div>
        ) : (
          /* Exception Table */
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="bg-white">
                <tr>
                  <th>Date</th>
                  <th>Technician</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th className="text-right">Hours</th>
                  <th className="text-center">Status</th>
                  <th>Reason</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.session_id}>
                    <td>
                      {row.check_in_at ? convertFromUTC(row.check_in_at).format('MMM D') : '—'}
                    </td>
                    <td>
                      <strong>{row.technician_name}</strong>
                    </td>
                    <td>
                      {row.check_in_at ? convertFromUTC(row.check_in_at).format('h:mm A') : '—'}
                    </td>
                    <td>
                      {row.check_out_at ? convertFromUTC(row.check_out_at).format('h:mm A') : (
                        <span className="text-muted">Open</span>
                      )}
                    </td>
                    <td className="text-right">
                      {this.formatHours(row.total_hours)}
                    </td>
                    <td className="text-center">
                      {this.getStatusBadge(row.status)}
                    </td>
                    <td>
                      <small className="text-muted">
                        {this.formatReasons(row.event_reasons)}
                      </small>
                    </td>
                    <td>
                      {row.last_note && (
                        <small className="text-muted" title={row.last_note}>
                          {row.last_note.length > 30
                            ? `${row.last_note.substring(0, 30)}...`
                            : row.last_note
                          }
                        </small>
                      )}
                    </td>
                    <td>
                      <Link
                        to={`/staff/${row.technician_id}/time`}
                        className="btn btn-sm btn-link text-muted p-0"
                        title="View technician time history"
                      >
                        <i className="fa-sharp fa-regular fa-external-link"></i>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}

export default ExceptionReport;
